import React, { useCallback, useMemo, useState } from 'react';
import { Button, Menu, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { ProgressButton, SimpleDialog } from 'modules/common/components';
import { menuItems, menuProps, useMenuStyles } from 'modules/notes/common';
import NoteActionMenutItem from 'modules/notes/common/components/NoteFooter/NoteActionMenutItem';
import { TimeSignalClassification } from 'graphql/types';
import { DialogCancelProps } from 'modules/common/hooks';
import { BulkSuggestionAcceptAllActions } from 'modules/timesheet-labels/BulkSuggestionAcceptAllActions';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';

export const messages = {
  cancelDialogYes: <FormattedMessage id="note.cancelDialog.cancelYes" />,
  cancelDialogNo: <FormattedMessage id="note.cancelDialog.cancelNo" />
};

const useStyles = makeStyles((theme) => ({
  save: {
    fontWeight: 600
  },
  cancel: {
    marginRight: theme.spacing(1)
  },
  actions: {
    display: 'flex',
    justifyContent: ({
      showConfirmAllSuggestion
    }: {
      showConfirmAllSuggestion: boolean;
    }) => (showConfirmAllSuggestion ? 'space-between' : 'flex-end')
  },
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: ({
      showConfirmAllSuggestion
    }: {
      showConfirmAllSuggestion: boolean;
    }) => (showConfirmAllSuggestion ? 'space-between' : 'flex-end'),
    padding: theme.spacing(2, 0)
  }
}));

type NoteDialogFooterProps = DialogCancelProps & {
  onSave: (
    onNoteDiscard: () => void,
    classificationTarget: TimeSignalClassification
  ) => void;
  isActionInProgress: boolean;
  selectedClassification?: TimeSignalClassification;
  cancelDialogBodyMessages: React.ReactNode[];
  cancelDialogTitle: React.ReactNode;
  disabled?: boolean;
  saveButtonMessage?: React.ReactNode;
  customLabels?: string;
  timesheetLabels?: string;
};

export const NoteDialogFooter: React.FC<NoteDialogFooterProps> = ({
  onNoteDiscard,
  closeCancelDialog,
  openCancel,
  selectedClassification,
  discardNote,
  onSave,
  isActionInProgress,
  cancelDialogBodyMessages,
  cancelDialogTitle,
  disabled = false,
  saveButtonMessage,
  customLabels,
  timesheetLabels
}: NoteDialogFooterProps) => {
  const menuClasses = useMenuStyles();
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();

  const showConfirmAllSuggestion = useMemo(() => {
    if (isSmartSuggestionEnabled && customLabels && timesheetLabels)
      return true;
    return false;
  }, [customLabels, isSmartSuggestionEnabled, timesheetLabels]);

  const classes = useStyles({ showConfirmAllSuggestion });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) =>
      setAnchorEl(anchorEl ? null : event.currentTarget),
    [anchorEl]
  );
  const onCloseMenu = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const onClose = useCallback(() => {
    onNoteDiscard();
    discardNote();
  }, [discardNote, onNoteDiscard]);

  const onHandleSave = useCallback(
    (classification: TimeSignalClassification) => {
      onSave(onClose, classification);
      onCloseMenu();
    },
    [onSave, onCloseMenu, onClose]
  );

  return (
    <div className={classes.container}>
      {showConfirmAllSuggestion && (
        <div>
          <BulkSuggestionAcceptAllActions
            customLabels={customLabels || ''}
            timesheetLabels={timesheetLabels || ''}
          />
        </div>
      )}
      <div className={classes.actions}>
        <Button
          data-testid="note-cancel-btn"
          variant="text"
          color="secondary"
          className={classes.cancel}
          onClick={onNoteDiscard}
          disabled={isActionInProgress}
        >
          <Typography color={'textSecondary'}>
            <FormattedMessage id="note.cancel" />
          </Typography>
        </Button>
        <ProgressButton
          data-testid="note-submit-btn"
          disabled={isActionInProgress || disabled}
          className={classes.save}
          loading={isActionInProgress}
          onClick={handleClick}
        >
          {saveButtonMessage ?? <FormattedMessage id="note.saveNote" />}
        </ProgressButton>
        <Menu
          elevation={0}
          data-testid="note-actions-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onCloseMenu}
          classes={menuClasses}
          {...menuProps}
        >
          {menuItems.map(
            ({ title, icon, classification: itemClassification }) => (
              <NoteActionMenutItem
                key={title}
                title={title}
                icon={icon}
                classification={itemClassification}
                selectedClassification={selectedClassification}
                onSave={onHandleSave}
              />
            )
          )}
        </Menu>
        {openCancel && (
          <SimpleDialog
            open={openCancel}
            onClose={closeCancelDialog}
            onPrimaryAction={closeCancelDialog}
            onSecondaryAction={onClose}
            title={cancelDialogTitle}
            messages={cancelDialogBodyMessages}
            primaryActionButtonTitle={messages.cancelDialogNo}
            secondaryActionButtonTitle={messages.cancelDialogYes}
          />
        )}
      </div>
    </div>
  );
};
